import { useState } from "react";
import { Plus } from "lucide-react";
import {
  useArchiveBankAccount,
  useBankAccounts,
  useCompanySettings,
  useSaveBankAccount,
  type BankAccount,
} from "@/api/settings";
import { BankAccountForm } from "@/components/forms/BankAccountForm";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { formatDate } from "@/lib/format";

type Editing = { mode: "new" } | { mode: "edit"; account: BankAccount } | null;

function AccountDetails({ account }: { account: BankAccount }) {
  const rows: [string, string][] = [
    ["Account name", account.account_name],
    ["Bank", account.branch ? `${account.bank}, ${account.branch}` : account.bank],
    ["Account no.", account.account_no],
    ["SWIFT", account.swift],
    ["IFSC", account.ifsc],
    ["IBAN", account.iban],
    ["Currency", account.currency],
  ];
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
      {rows
        .filter(([, value]) => value)
        .map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="break-words">{value}</dd>
          </div>
        ))}
    </dl>
  );
}

export function BankAccountsPanel() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const accounts = useBankAccounts(includeArchived);
  const company = useCompanySettings();
  const save = useSaveBankAccount();
  const archive = useArchiveBankAccount();

  return (
    <div className="flex flex-col gap-4">
      <Alert title="Confirm bank details in writing">
        Under SLA clause 6.4, send new bank details to clients on letterhead signed by a director
        before they appear on invoices, and record the date the letter was sent.
      </Alert>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Checkbox
          id="bank-accounts-archived"
          label="Show archived accounts"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
        {editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            Add bank account
          </Button>
        )}
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New bank account" : `Edit ${editing.account.label}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BankAccountForm
              key={
                editing.mode === "edit" ? `${editing.account.id}-${editing.account.version}` : "new"
              }
              initial={editing.mode === "edit" ? editing.account : null}
              defaultAccountName={company.data?.short_name}
              submitLabel={editing.mode === "new" ? "Add account" : "Save account"}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing:
                    editing.mode === "edit"
                      ? { id: editing.account.id, version: editing.account.version }
                      : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void accounts.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      <RequestError error={archive.error} onReload={() => void accounts.refetch()} />

      {accounts.isPending ? (
        <p className="text-muted-foreground">Loading bank accounts…</p>
      ) : accounts.isError ? (
        <RequestError error={accounts.error} />
      ) : accounts.data.length === 0 ? (
        <EmptyState
          title="No bank accounts yet"
          description="Until an account is added, invoices say that bank details follow on signed letterhead."
        />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2">
          {accounts.data.map((account) => {
            const isArchived = account.archived_at !== null;
            return (
              <li key={account.id}>
                <Card className="h-full">
                  <CardContent className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{account.label}</span>
                      {account.is_default && <Badge>Default</Badge>}
                      {isArchived && <Badge variant="muted">Archived</Badge>}
                      {account.confirmation_letter_sent ? (
                        <Badge variant="success">
                          Letter sent {formatDate(account.letter_sent_on)}
                        </Badge>
                      ) : (
                        <Badge variant="warning">Letter not sent</Badge>
                      )}
                    </div>
                    <AccountDetails account={account} />
                    <div className="flex flex-wrap gap-2">
                      {!isArchived && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing({ mode: "edit", account })}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={archive.isPending || (account.is_default && !isArchived)}
                        title={
                          account.is_default && !isArchived
                            ? "Make another account the default before archiving this one"
                            : undefined
                        }
                        onClick={() =>
                          archive.mutate({
                            id: account.id,
                            archived: !isArchived,
                            version: account.version,
                          })
                        }
                      >
                        {isArchived ? "Restore" : "Archive"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
